import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import {
  createUrl as apiCreateUrl,
  deleteUrl as apiDeleteUrl,
  getMyStats,
  getMyUrls,
  getUrlClicks,
  CreateUrlPayload,
  UserStatsResponse,
  UrlClick,
  UrlItem,
} from "../services/api";

type SortKey = "created_at" | "click_count" | "short_url";
type SortDirection = "asc" | "desc";
type StatusFilter = "all" | "active" | "inactive";

type RefreshOptions = {
  silent?: boolean;
};

type AnalyticsContextValue = {
  urls: UrlItem[];
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  selectedUrl: UrlItem | null;
  selectedUrlClicks: UrlClick[];
  selectedUrlId: number | null;
  loadingSelectedDetails: boolean;
  detailsError: string | null;
  searchQuery: string;
  sortKey: SortKey;
  sortDirection: SortDirection;
  statusFilter: StatusFilter;
  page: number;
  pageSize: number;
  totalPages: number;
  totalResults: number;
  paginatedUrls: UrlItem[];
  autoRefreshEnabled: boolean;
  statsLoading: boolean;
  statsError: string | null;
  statsConsistencyMismatch: boolean;
  totalUrls: number;
  totalClicks: number;
  activeUrls: number;
  creatingUrl: boolean;
  createUrlError: string | null;
  createUrlSuccess: string | null;
  deletingUrlId: number | null;
  deleteUrlError: string | null;
  setSearchQuery: (value: string) => void;
  setSortKey: (value: SortKey) => void;
  toggleSortDirection: () => void;
  setStatusFilter: (value: StatusFilter) => void;
  setPage: (value: number) => void;
  setPageSize: (value: number) => void;
  setAutoRefreshEnabled: (value: boolean) => void;
  selectUrl: (urlId: number) => void;
  refreshUrls: (options?: RefreshOptions) => Promise<void>;
  refreshSelectedUrlClicks: () => Promise<void>;
  createNewUrl: (payload: CreateUrlPayload) => Promise<UrlItem | null>;
  removeUrl: (urlId: number) => Promise<boolean>;
  clearMutationMessages: () => void;
  exportFilteredAsCsv: () => void;
};

const AnalyticsContext = createContext<AnalyticsContextValue | undefined>(undefined);

function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === "string") return detail;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Analytics-Daten konnten nicht geladen werden.";
}

function csvCell(value: string | number | null | undefined): string {
  const text = String(value ?? "");
  return `"${text.replace(/"/g, '""')}"`;
}

export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const [urls, setUrls] = useState<UrlItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedUrlId, setSelectedUrlId] = useState<number | null>(null);
  const [clicksByUrl, setClicksByUrl] = useState<Record<number, UrlClick[]>>({});
  const [loadingClicksByUrl, setLoadingClicksByUrl] = useState<Record<number, boolean>>({});
  const [detailsError, setDetailsError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("created_at");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [userStats, setUserStats] = useState<UserStatsResponse | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [creatingUrl, setCreatingUrl] = useState(false);
  const [createUrlError, setCreateUrlError] = useState<string | null>(null);
  const [createUrlSuccess, setCreateUrlSuccess] = useState<string | null>(null);
  const [deletingUrlId, setDeletingUrlId] = useState<number | null>(null);
  const [deleteUrlError, setDeleteUrlError] = useState<string | null>(null);

  const refreshUrls = useCallback(async (options?: RefreshOptions) => {
    const isSilent = Boolean(options?.silent);
    if (isSilent) {
      setRefreshing(true);
    } else {
      setLoading(true);
      setStatsLoading(true);
    }

    try {
      const [urlsResult, statsResult] = await Promise.allSettled([
        getMyUrls(0, 500),
        getMyStats(),
      ]);

      if (urlsResult.status === "fulfilled") {
        setUrls(urlsResult.value);
        setError(null);
      } else {
        setError(getErrorMessage(urlsResult.reason));
      }

      if (statsResult.status === "fulfilled") {
        setUserStats(statsResult.value);
        setStatsError(null);
      } else {
        setStatsError(getErrorMessage(statsResult.reason));
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      if (isSilent) {
        setRefreshing(false);
      } else {
        setLoading(false);
        setStatsLoading(false);
      }
    }
  }, []);

  const loadClicksForUrl = useCallback(
    async (urlId: number, force = false) => {
      if (!force && clicksByUrl[urlId]) return;

      setLoadingClicksByUrl((current) => ({ ...current, [urlId]: true }));
      setDetailsError(null);
      try {
        const clicks = await getUrlClicks(urlId, 0, 500);
        setClicksByUrl((current) => ({ ...current, [urlId]: clicks }));
      } catch (err) {
        setDetailsError(getErrorMessage(err));
      } finally {
        setLoadingClicksByUrl((current) => ({ ...current, [urlId]: false }));
      }
    },
    [clicksByUrl]
  );

  const selectUrl = useCallback(
    (urlId: number) => {
      setSelectedUrlId(urlId);
      void loadClicksForUrl(urlId);
    },
    [loadClicksForUrl]
  );

  const refreshSelectedUrlClicks = useCallback(async () => {
    if (!selectedUrlId) return;
    await loadClicksForUrl(selectedUrlId, true);
  }, [selectedUrlId, loadClicksForUrl]);

  const clearMutationMessages = useCallback(() => {
    setCreateUrlError(null);
    setCreateUrlSuccess(null);
    setDeleteUrlError(null);
  }, []);

  const createNewUrl = useCallback(
    async (payload: CreateUrlPayload): Promise<UrlItem | null> => {
      setCreatingUrl(true);
      setCreateUrlError(null);
      setCreateUrlSuccess(null);
      setDeleteUrlError(null);

      try {
        const created = await apiCreateUrl(payload);
        setCreateUrlSuccess("Kurzlink wurde erfolgreich erstellt.");
        await refreshUrls({ silent: true });
        return created;
      } catch (err) {
        setCreateUrlError(getErrorMessage(err));
        return null;
      } finally {
        setCreatingUrl(false);
      }
    },
    [refreshUrls]
  );

  const removeUrl = useCallback(
    async (urlId: number): Promise<boolean> => {
      setDeletingUrlId(urlId);
      setDeleteUrlError(null);
      setCreateUrlSuccess(null);

      if (selectedUrlId === urlId) {
        setSelectedUrlId(null);
      }

      try {
        await apiDeleteUrl(urlId);
        setClicksByUrl((current) => {
          const next = { ...current };
          delete next[urlId];
          return next;
        });
        await refreshUrls({ silent: true });
        return true;
      } catch (err) {
        setDeleteUrlError(getErrorMessage(err));
        return false;
      } finally {
        setDeletingUrlId(null);
      }
    },
    [refreshUrls, selectedUrlId]
  );

  useEffect(() => {
    void refreshUrls();
  }, [refreshUrls]);

  useEffect(() => {
    if (!autoRefreshEnabled) return undefined;
    const interval = window.setInterval(() => {
      void refreshUrls({ silent: true });
      if (selectedUrlId) {
        void loadClicksForUrl(selectedUrlId, true);
      }
    }, 30000);
    return () => window.clearInterval(interval);
  }, [autoRefreshEnabled, loadClicksForUrl, refreshUrls, selectedUrlId]);

  const filteredSortedUrls = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const list = urls.filter((url) => {
      const matchesQuery =
        query.length === 0 ||
        url.short_url.toLowerCase().includes(query) ||
        url.original_url.toLowerCase().includes(query);

      if (!matchesQuery) return false;
      if (statusFilter === "all") return true;
      if (statusFilter === "active") return url.is_active;
      return !url.is_active;
    });

    list.sort((a, b) => {
      let result = 0;
      if (sortKey === "click_count") {
        result = a.click_count - b.click_count;
      } else if (sortKey === "short_url") {
        result = a.short_url.localeCompare(b.short_url);
      } else {
        result = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      }
      return sortDirection === "asc" ? result : -result;
    });

    return list;
  }, [searchQuery, sortDirection, sortKey, statusFilter, urls]);

  const totalResults = filteredSortedUrls.length;
  const totalPages = Math.max(1, Math.ceil(totalResults / pageSize));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const paginatedUrls = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredSortedUrls.slice(start, start + pageSize);
  }, [filteredSortedUrls, page, pageSize]);

  const selectedUrl = useMemo(() => {
    if (!selectedUrlId) return null;
    return urls.find((url) => url.id === selectedUrlId) ?? null;
  }, [selectedUrlId, urls]);

  useEffect(() => {
    if (selectedUrlId && !urls.some((url) => url.id === selectedUrlId)) {
      setSelectedUrlId(null);
    }
  }, [selectedUrlId, urls]);

  const selectedUrlClicks = selectedUrl ? clicksByUrl[selectedUrl.id] || [] : [];
  const loadingSelectedDetails =
    selectedUrl !== null ? Boolean(loadingClicksByUrl[selectedUrl.id]) : false;

  const derivedTotalClicks = useMemo(
    () => urls.reduce((acc, item) => acc + item.click_count, 0),
    [urls]
  );
  const derivedActiveUrls = useMemo(
    () => urls.filter((item) => item.is_active).length,
    [urls],
  );
  const derivedTotalUrls = urls.length;

  const totalUrls = userStats?.total_urls ?? derivedTotalUrls;
  const totalClicks = userStats?.total_clicks ?? derivedTotalClicks;
  const activeUrls = userStats?.active_urls ?? derivedActiveUrls;
  const statsConsistencyMismatch = Boolean(
    userStats &&
      (userStats.total_urls !== derivedTotalUrls ||
        userStats.total_clicks !== derivedTotalClicks ||
        userStats.active_urls !== derivedActiveUrls),
  );

  const toggleSortDirection = useCallback(() => {
    setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
  }, []);

  const exportFilteredAsCsv = useCallback(() => {
    const header = ["Kurzlink", "Ziel-URL", "Klicks", "Status", "Erstellt am"];
    const lines = filteredSortedUrls.map((item) =>
      [
        csvCell(item.short_url),
        csvCell(item.original_url),
        csvCell(item.click_count),
        csvCell(item.is_active ? "Aktiv" : "Inaktiv"),
        csvCell(new Date(item.created_at).toLocaleString("de-DE")),
      ].join(",")
    );

    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `linkyshorty-analytics-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }, [filteredSortedUrls]);

  const value: AnalyticsContextValue = {
    urls,
    loading,
    refreshing,
    error,
    selectedUrl,
    selectedUrlClicks,
    selectedUrlId,
    loadingSelectedDetails,
    detailsError,
    searchQuery,
    sortKey,
    sortDirection,
    statusFilter,
    page,
    pageSize,
    totalPages,
    totalResults,
    paginatedUrls,
    autoRefreshEnabled,
    statsLoading,
    statsError,
    statsConsistencyMismatch,
    totalUrls,
    totalClicks,
    activeUrls,
    creatingUrl,
    createUrlError,
    createUrlSuccess,
    deletingUrlId,
    deleteUrlError,
    setSearchQuery,
    setSortKey,
    toggleSortDirection,
    setStatusFilter,
    setPage,
    setPageSize,
    setAutoRefreshEnabled,
    selectUrl,
    refreshUrls,
    refreshSelectedUrlClicks,
    createNewUrl,
    removeUrl,
    clearMutationMessages,
    exportFilteredAsCsv,
  };

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}

export function useAnalyticsContext() {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error("useAnalyticsContext muss innerhalb von AnalyticsProvider verwendet werden.");
  }
  return context;
}
